/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import { Quotes, SealCheck, Star } from "@phosphor-icons/react";
import { motion } from "framer-motion";

type Testimonial = {
  quote: string;
  name: string;
  title: string;
  rating: number;
  feature?: string;
};

const testimonials: Testimonial[][] = [
  [
    {
      name: "N. Elnour",
      title: "Software Engineer",
      rating: 5,
      feature: "AI Writing",
      quote:
        "This is really a thank you for CareerBldr. Drafting resumes was never a strength of mine, so this app really made the whole process easy and smooth! The AI suggestions were spot on.",
    },
    {
      name: "R. Sinnot",
      title: "Marketing Manager",
      rating: 5,
      feature: "Auto-Tailoring",
      quote:
        "Not only helped me get a job, but also helped my partner and my childhood friend. The AI tailoring feature is incredible, and each resume felt personalized for the specific role.",
    },
    {
      name: "D. Vasquez",
      title: "Data Analyst",
      rating: 5,
      feature: "Cover Letters",
      quote:
        "The cover letter generator alone sold me on CareerBldr. I used to spend an hour per application writing cover letters. Now it takes 30 seconds and they're better than what I wrote manually.",
    },
  ],
  [
    {
      name: "S. Bhaije",
      title: "University Graduate",
      rating: 5,
      feature: "ATS Score",
      quote:
        "This is one of the best resume-building tools I have ever found. The ATS scoring gave me confidence that my resume would actually get seen. Recommended it to all my university friends.",
    },
    {
      name: "M. Fritza",
      title: "Junior Developer",
      rating: 5,
      feature: "AI Writing",
      quote:
        "Easy to use, intuitive, and much more practical than many others. The AI wrote better bullet points than I could have. The DOCX export was perfect for the companies that requested Word format.",
    },
    {
      name: "A. Kimura",
      title: "HR Specialist",
      rating: 5,
      feature: "Job Matching",
      quote:
        "As someone who reviews resumes daily, I can tell you CareerBldr produces genuinely competitive applications. The job matching feature helps candidates hit the keywords we actually look for.",
    },
  ],
  [
    {
      name: "K. Lietzau",
      title: "Product Designer",
      rating: 5,
      feature: "AI Writing",
      quote:
        "The moment I saw how the AI improved my bullet points, I closed all the other CV sites I was considering. Got 3 interview callbacks in the first week of applying with my new resume.",
    },
    {
      name: "P. Jignesh",
      title: "Mechanical Engineer",
      rating: 5,
      feature: "AI Assistant",
      quote:
        "I'm not a tech person at all, but CareerBldr made everything simple. The AI assistant answered all my questions about formatting and content. Landed my dream role.",
    },
    {
      name: "L. Okonkwo",
      title: "Project Manager",
      rating: 5,
      feature: "ATS Score",
      quote:
        "After months of applying with no callbacks, I ran my resume through the ATS checker and scored 47. After the AI improvements, I hit 91. Three interviews in the first week.",
    },
  ],
];

const Stars = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {[...Array(count)].map((_, i) => (
      <Star key={i} weight="fill" className="h-3.5 w-3.5 text-yellow-500" />
    ))}
  </div>
);

export const TestimonialsSection = () => (
  <section id="testimonials" className="container relative space-y-12 py-24 sm:py-32">
    <motion.div
      className="mx-auto max-w-2xl space-y-4 text-center"
      viewport={{ once: true }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">
        {t`Testimonials`}
      </p>
      <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
        {t`Loved by job seekers worldwide`}
      </h2>
      <p className="text-base leading-relaxed text-foreground/60">
        {t`Here's what CareerBldr users have to say about their experience.`}
      </p>
    </motion.div>

    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {testimonials.map((column, colIndex) => (
        <div key={colIndex} className="space-y-6">
          {column.map((testimonial, index) => (
            <motion.figure
              key={index}
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: (colIndex * 3 + index) * 0.07 } }}
              className="relative overflow-hidden rounded-xl border border-border/50 bg-background p-6"
            >
              <Quotes size={48} className="absolute -right-2 -top-1 text-foreground/5" />

              <div className="flex items-center justify-between">
                <Stars count={testimonial.rating} />
                {testimonial.feature && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                    {testimonial.feature}
                  </span>
                )}
              </div>

              <blockquote className="mt-3 text-sm leading-relaxed text-foreground/70">
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>

              <figcaption className="mt-4 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    {testimonial.name}
                    <SealCheck size={14} weight="fill" className="text-blue-500" />
                    <span className="text-[10px] font-normal text-foreground/40">Verified</span>
                  </div>
                  <div className="text-xs text-foreground/50">{testimonial.title}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      ))}
    </div>
  </section>
);
